import { CoreTypes, DEFAULT_FONT_FAMILY, type Id, type ObjectRecord, type Style, type TextStyle } from '@vp/shared-types';
import { ulid } from './ulid';

export const CORE_FORMAT_VERSION = 1;

export const DEFAULT_TEXT_STYLE: TextStyle = {
  fontSize: 18,
  fontFamily: DEFAULT_FONT_FAMILY,
  color: '#1f2937',
  align: 'center',
  valign: 'middle',
  lineHeight: 1.25,
};

export function defaultStyle(type: string): Style {
  switch (type) {
    case CoreTypes.text:
      return { stroke: null, fill: { kind: 'none' }, opacity: 1, text: { ...DEFAULT_TEXT_STYLE, fontSize: 24, align: 'left', valign: 'top' } };
    case CoreTypes.sticker:
      return {
        stroke: null,
        fill: { kind: 'solid', color: '#fde68a' },
        opacity: 1,
        cornerRadius: 4,
        text: { ...DEFAULT_TEXT_STYLE, fontSize: 20 },
      };
    case CoreTypes.frame:
      return {
        stroke: { color: '#9ca3af', width: 1, dash: 'solid' },
        fill: { kind: 'solid', color: '#ffffff' },
        opacity: 1,
        text: { ...DEFAULT_TEXT_STYLE, fontSize: 14, color: '#6b7280', align: 'left', valign: 'top' },
      };
    case CoreTypes.image:
    case CoreTypes.group:
    case CoreTypes.diagram:
      return { stroke: null, fill: { kind: 'none' }, opacity: 1 };
    case CoreTypes.line:
    case CoreTypes.connector:
      return { stroke: { color: '#1f2937', width: 2, dash: 'solid' }, fill: { kind: 'none' }, opacity: 1 };
    case CoreTypes.pen:
      return { stroke: { color: '#1f2937', width: 3, dash: 'solid' }, fill: { kind: 'none' }, opacity: 1 };
    case CoreTypes.path:
      return { stroke: { color: '#1f2937', width: 2, dash: 'solid' }, fill: { kind: 'none' }, opacity: 1 };
    default:
      return {
        stroke: { color: '#1f2937', width: 2, dash: 'solid' },
        fill: { kind: 'solid', color: '#ffffff' },
        opacity: 1,
        text: { ...DEFAULT_TEXT_STYLE },
      };
  }
}

export function defaultProps(type: string): Record<string, unknown> {
  switch (type) {
    case CoreTypes.text:
      return { text: '' };
    case CoreTypes.sticker:
      return { text: '' };
    case CoreTypes.frame:
      return { title: '' };
    case CoreTypes.line:
      return { points: [0, 0, 100, 0], arrowStart: false, arrowEnd: false };
    case CoreTypes.connector:
      return {
        start: { point: { x: 0, y: 0 } },
        end: { point: { x: 100, y: 0 } },
        route: 'orthogonal',
        arrowStart: false,
        arrowEnd: true,
        path: [0, 0, 100, 0],
      };
    case CoreTypes.image:
      return { attachment: null };
    case CoreTypes.pen:
      return { points: [0, 0, 1, 1] };
    case CoreTypes.path:
      // nodes: [x, y, inX, inY, outX, outY]* in local coordinates; points: the flattened outline
      return { nodes: [0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 1], points: [0, 0, 1, 1], closed: false };
    case CoreTypes.group:
      return {};
    case CoreTypes.diagram:
      return { source: '', lang: 'dskhema', langVersion: 2 };
    default:
      return { text: '' };
  }
}

export interface NewObjectInit {
  type: string;
  x: number;
  y: number;
  w: number;
  h: number;
  z: string;
  id?: Id;
  rotation?: number;
  parentId?: Id | null;
  props?: Record<string, unknown>;
  style?: Partial<Style>;
  formatVersion?: number;
  name?: string;
}

export function makeObject(canvasId: Id, init: NewObjectInit): ObjectRecord {
  const isCore = init.type.startsWith('core:');
  return {
    id: init.id ?? ulid(),
    canvasId,
    type: init.type,
    formatVersion: init.formatVersion ?? (isCore ? CORE_FORMAT_VERSION : 0),
    parentId: init.parentId ?? null,
    z: init.z,
    x: init.x,
    y: init.y,
    w: init.w,
    h: init.h,
    rotation: init.rotation ?? 0,
    locked: false,
    ...(init.name !== undefined ? { name: init.name } : {}),
    style: { ...defaultStyle(init.type), ...(init.style ?? {}) } as Style,
    props: { ...(isCore ? defaultProps(init.type) : {}), ...(init.props ?? {}) },
  };
}
